import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { useToast } from '../../components/ui/use-toast';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Search, 
  Settings, 
  TrendingUp, 
  Building, 
  FileText, 
  Users,
  DollarSign,
  MapPin,
  Plus,
  Filter,
  MoreVertical,
  Phone,
  Mail,
  Calendar,
  Target,
  Zap,
  Star
} from 'lucide-react';
import OrganizationPricingModal from './components/OrganizationPricingModal';
import NewOrganizationModal from './components/NewOrganizationModal';

const PricingOrganizationsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [organizations, setOrganizations] = useState([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showNewOrganizationModal, setShowNewOrganizationModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [monthlyStats, setMonthlyStats] = useState({});
  const [filters, setFilters] = useState({
    contractStatus: 'all', // all, active, expired, pending
    hasFreeCases: 'all', // all, yes, no
    hasContactInfo: 'all' // all, yes, no
  });
  const { toast } = useToast();
  
  // Sadece super_admin kurum oluşturabilir
  const canManageInstitutions = ['super_admin', 'admin'].includes(user?.role);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Close filters when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilters && !event.target.closest('.filter-dropdown')) {
        setShowFilters(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilters]);

  useEffect(() => {
    let filtered = organizations;

    // Search filter
    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(org =>
        org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (org.code && org.code.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Contract status filter
    if (filters.contractStatus !== 'all') {
      const currentDate = new Date().toISOString().split('T')[0];
      filtered = filtered.filter(org => {
        switch (filters.contractStatus) {
          case 'active':
            return !org.contract_end_date || org.contract_end_date > currentDate;
          case 'expired':
            return org.contract_end_date && org.contract_end_date <= currentDate;
          case 'pending':
            return !org.contract_start_date || org.contract_start_date > currentDate;
          default:
            return true;
        }
      });
    }

    // Free cases filter
    if (filters.hasFreeCases !== 'all') {
      filtered = filtered.filter(org => {
        const hasFree = org.free_case_limit && org.free_case_limit > 0;
        return filters.hasFreeCases === 'yes' ? hasFree : !hasFree;
      });
    }

    // Contact info filter
    if (filters.hasContactInfo !== 'all') {
      filtered = filtered.filter(org => {
        const hasContact = org.contact_person || org.contact_phone || org.contact_email;
        return filters.hasContactInfo === 'yes' ? hasContact : !hasContact;
      });
    }

    setFilteredOrganizations(filtered);
  }, [searchQuery, organizations, filters]);

  const isParentInstitution = user?.role === 'external_parent_institution';
  const isExternalInstitution = user?.role === 'external_institution';

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const [institutionsRes, statsRes] = await Promise.all([
        api.get('/institutions?mode=pricing_summary'),
        api.get('/pricing/organizations/batch-stats').catch((error) => {
          console.error('❌ Batch stats error:', error);
          return null;
        }),
      ]);

      if (institutionsRes.data.success) {
        let allOrgs = institutionsRes.data.data.filter((org, index, self) =>
          index === self.findIndex((o) => o.id === org.id)
        );

        // external_parent_institution: sadece kendi alt kurumlarını görür
        // external_institution: sadece kendi kurumunu görür
        let uniqueOrgs = allOrgs;
        if (isParentInstitution && user?.institution_id) {
          uniqueOrgs = allOrgs.filter(org => org.parent_institution_id === user.institution_id);
        } else if (isExternalInstitution && user?.institution_id) {
          uniqueOrgs = allOrgs.filter(org => org.id === user.institution_id);
        }

        setOrganizations(uniqueOrgs);
        setFilteredOrganizations(uniqueOrgs);

        // Batch istatistikler başarılı geldiyse kullan, aksi halde varsayılanları oluştur
        if (statsRes && statsRes.data?.success) {
          setMonthlyStats(statsRes.data.data);
          console.log('✅ Batch stats loaded successfully');
        } else {
          console.warn('⚠️ Batch stats failed or unavailable, using defaults');
          const defaultStats = {};
          uniqueOrgs.forEach((org) => {
            const monthlyLimit = org.free_case_limit || org.free_cases_per_month || 50;
            const zone1Price = Number(org.zone_1_price) || 0;
            defaultStats[org.id] = {
              total_cases: 0,
              free_cases: 0,
              paid_cases: 0,
              total_revenue: 0,
              avg_paid_fee: 0,
              monthly_limit: monthlyLimit,
              remaining_free: monthlyLimit,
              free_case_period: org.free_case_period || 'monthly',
              zone_1_price: zone1Price,
            };
          });
          setMonthlyStats(defaultStats);
        }
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Kurumlar yüklenirken hata oluştu"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePricingModalClose = () => {
    setShowPricingModal(false);
    setSelectedOrganization(null);
    // fetchOrganizations(); // Geçici olarak devre dışı - performance için
  };

  const openPricingModal = (organization) => {
    setSelectedOrganization(organization);
    setShowPricingModal(true);
  };

  const openNewOrganizationModal = () => {
    setShowNewOrganizationModal(true);
  };

  const handleNewOrganizationModalClose = () => {
    setShowNewOrganizationModal(false);
  };

  const handleNewOrganizationSuccess = () => {
    fetchOrganizations(); // Refresh the organizations list
  };

  const getFreeCaseText = (org) => {
    if (!org.free_case_limit || org.free_case_limit === 0) {
      return 'Ücretsiz vaka yok';
    }
    const period = org.free_case_period === 'monthly' ? 'aylık' : 
                   org.free_case_period === 'yearly' ? 'yıllık' : 'toplam';
    return `İlk ${org.free_case_limit} vaka ücretsiz (${period})`;
  };

  const getStats = () => {
    const currentDate = new Date().toISOString().split('T')[0];
    const activeContracts = organizations.filter(org => 
      !org.contract_end_date || org.contract_end_date > currentDate
    ).length;
    
    const pendingContracts = organizations.filter(org => 
      org.contract_start_date && org.contract_start_date > currentDate
    ).length;
    
    const expiredContracts = organizations.filter(org => 
      org.contract_end_date && org.contract_end_date <= currentDate
    ).length;

    return { activeContracts, pendingContracts, expiredContracts };
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header with Gradient */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative container mx-auto px-4 py-8 md:py-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-6 lg:space-y-0">
            <div className="flex items-center space-x-4 md:space-x-6">
              <div className="p-3 md:p-4 bg-white/10 backdrop-blur-sm rounded-2xl">
                <Building className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-4xl font-bold text-white mb-1 md:mb-2">
                  Kurumlar
                </h1>
                <p className="text-white/80 text-sm md:text-lg">
                  Anlaşmalı kurumların fiyatlandırmalarını yönetin
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-3">
                <div className="flex items-center space-x-3">
                  <Building className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold text-lg">
                    {organizations.length}
                  </span>
                  <span className="text-white/80">Kurum</span>
                </div>
              </div>
              {canManageInstitutions && (
                <Button 
                  className="bg-white text-gray-900 hover:bg-gray-50 hover:shadow-xl shadow-lg font-bold px-8 py-3 text-base transition-all duration-300 transform hover:scale-105 border border-gray-200"
                  size="lg"
                  onClick={openNewOrganizationModal}
                >
                  <Plus className="w-5 h-5 mr-3 text-gray-700" />
                  <span className="whitespace-nowrap text-gray-900">Yeni Kurum</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Modern Search and Filters */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row items-stretch md:items-center space-y-4 md:space-y-0 md:space-x-4">
            <div className="relative flex-1 max-w-full md:max-w-2xl">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Kurum adı veya kodu ile ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-3 md:py-4 text-base md:text-lg border-0 bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 w-full"
              />
            </div>
            <div className="relative filter-dropdown">
              <Button 
                variant="outline" 
                size="lg" 
                onClick={() => setShowFilters(!showFilters)}
                className={`w-full md:w-auto bg-white/60 backdrop-blur-sm border-0 rounded-2xl shadow-lg hover:bg-white/80 transition-all duration-300 ${
                  showFilters ? 'bg-blue-100 text-blue-700' : ''
                }`}
              >
                <Filter className="w-5 h-5 mr-2" />
                <span>Filtrele</span>
              </Button>
              
              {/* Filter Dropdown */}
              {showFilters && (
                <div className="absolute top-full mt-2 right-0 left-0 md:left-auto w-full md:w-80 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-6 z-20">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Anlaşma Durumu
                      </label>
                      <Select value={filters.contractStatus} onValueChange={(value) => 
                        setFilters(prev => ({...prev, contractStatus: value}))
                      }>
                        <SelectTrigger className="rounded-xl border-0 bg-gray-50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tümü</SelectItem>
                          <SelectItem value="active">Aktif</SelectItem>
                          <SelectItem value="expired">Süresi Dolmuş</SelectItem>
                          <SelectItem value="pending">Beklemede</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Ücretsiz Vaka
                      </label>
                      <Select value={filters.hasFreeCases} onValueChange={(value) => 
                        setFilters(prev => ({...prev, hasFreeCases: value}))
                      }>
                        <SelectTrigger className="rounded-xl border-0 bg-gray-50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tümü</SelectItem>
                          <SelectItem value="yes">Var</SelectItem>
                          <SelectItem value="no">Yok</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        İletişim Bilgisi
                      </label>
                      <Select value={filters.hasContactInfo} onValueChange={(value) => 
                        setFilters(prev => ({...prev, hasContactInfo: value}))
                      }>
                        <SelectTrigger className="rounded-xl border-0 bg-gray-50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tümü</SelectItem>
                          <SelectItem value="yes">Var</SelectItem>
                          <SelectItem value="no">Yok</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex space-x-3 pt-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          setFilters({
                            contractStatus: 'all',
                            hasFreeCases: 'all',
                            hasContactInfo: 'all'
                          });
                        }}
                        className="flex-1 rounded-xl"
                      >
                        Temizle
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => setShowFilters(false)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                      >
                        Uygula
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-lg border border-white/20">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
              <div>
                <p className="text-xs md:text-sm font-medium text-gray-600">Toplam Kurum</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-900">{organizations.length}</p>
              </div>
              <div className="p-2 md:p-3 bg-blue-100 rounded-2xl self-end md:self-auto">
                <Building className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-lg border border-white/20">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
              <div>
                <p className="text-xs md:text-sm font-medium text-gray-600">Aktif Anlaşma</p>
                <p className="text-2xl md:text-3xl font-bold text-green-600">
                  {getStats().activeContracts}
                </p>
              </div>
              <div className="p-2 md:p-3 bg-green-100 rounded-2xl self-end md:self-auto">
                <Target className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-lg border border-white/20">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
              <div>
                <p className="text-xs md:text-sm font-medium text-gray-600">Beklemede</p>
                <p className="text-2xl md:text-3xl font-bold text-yellow-600">{getStats().pendingContracts}</p>
              </div>
              <div className="p-2 md:p-3 bg-yellow-100 rounded-2xl self-end md:self-auto">
                <Calendar className="w-5 h-5 md:w-6 md:h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-lg border border-white/20">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
              <div>
                <p className="text-xs md:text-sm font-medium text-gray-600">Süresi Dolmuş</p>
                <p className="text-2xl md:text-3xl font-bold text-red-600">{getStats().expiredContracts}</p>
              </div>
              <div className="p-2 md:p-3 bg-red-100 rounded-2xl self-end md:self-auto">
                <Zap className="w-5 h-5 md:w-6 md:h-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Modern Organizations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          {filteredOrganizations.map((organization, index) => (
            <div 
              key={organization.id} 
              className="group bg-white/70 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/20 hover:shadow-2xl hover:bg-white/80 transition-all duration-500 hover:-translate-y-2"
              style={{
                animationDelay: `${index * 100}ms`,
                animation: 'fadeInUp 0.6s ease-out forwards'
              }}
            >
              {/* Organization Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl">
                    <Building className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                      {organization.name}
                    </h3>
                    {organization.code && (
                      <Badge className="mt-1 bg-blue-100 text-blue-700 border-0">
                        {organization.code}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Organization Info */}
              <div className="space-y-4 mb-6">
                {/* Free Cases Info */}
                <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-xl">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FileText className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-900">
                      {getFreeCaseText(organization)}
                    </p>
                  </div>
                </div>

                {/* Monthly Statistics */}
                {monthlyStats[organization.id] && (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-xl">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <TrendingUp className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-sm font-medium text-blue-900">
                            {monthlyStats[organization.id].free_case_period === 'yearly' ? 'Bu Yıl' : 
                             monthlyStats[organization.id].free_case_period === 'total' ? 'Toplam' : 
                             'Bu Ay'}: {monthlyStats[organization.id].total_cases} vaka
                          </p>
                          <span className="text-xs text-blue-600 font-medium">
                            {monthlyStats[organization.id].total_cases > 0 ? 
                              `${Math.round((monthlyStats[organization.id].free_cases / monthlyStats[organization.id].total_cases) * 100)}% ücretsiz` : 
                              '0% ücretsiz'
                            }
                          </span>
                        </div>
                        <div className="flex space-x-1 mb-2">
                          {monthlyStats[organization.id].free_cases > 0 && (
                            <div 
                              className="h-2 bg-green-500 rounded-l-full"
                              style={{
                                width: `${(monthlyStats[organization.id].free_cases / monthlyStats[organization.id].total_cases) * 100}%`
                              }}
                            ></div>
                          )}
                          {monthlyStats[organization.id].paid_cases > 0 && (
                            <div 
                              className="h-2 bg-blue-500 rounded-r-full"
                              style={{
                                width: `${(monthlyStats[organization.id].paid_cases / monthlyStats[organization.id].total_cases) * 100}%`
                              }}
                            ></div>
                          )}
                        </div>
                        <p className="text-xs text-blue-700">
                          {monthlyStats[organization.id].free_cases} ücretsiz, {monthlyStats[organization.id].paid_cases} ücretli
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-xl">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <DollarSign className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <p className="text-sm font-medium text-purple-900">
                            Toplam Gelir: {Number(monthlyStats[organization.id].total_revenue ?? 0).toLocaleString('tr-TR')} ₺
                          </p>
                          <span className="text-xs text-purple-600 font-medium">
                            {(monthlyStats[organization.id].total_cases ?? 0)} vaka
                          </span>
                        </div>
                        {(() => {
                          const stats = monthlyStats[organization.id];
                          const fixedAmt = Number(stats.contract_fixed_amount ?? 0);
                          const zone1 = Number(stats.zone_1_price ?? 0);
                          const freeCases = stats.free_cases ?? 0;
                          const paidCases = stats.paid_cases ?? 0;
                          const freeLimit = stats.monthly_limit ?? 0;

                          if (fixedAmt > 0) {
                            return (
                              <>
                                <p className="text-xs text-purple-700">
                                  Sabit Anlaşma: {fixedAmt.toLocaleString('tr-TR')} ₺ ({freeLimit} vaka kotası)
                                </p>
                                {paidCases > 0 && zone1 > 0 && (
                                  <p className="text-xs text-purple-600 mt-0.5">
                                    + {paidCases} aşım vakası × {zone1.toLocaleString('tr-TR')} ₺ = {(paidCases * zone1).toLocaleString('tr-TR')} ₺
                                  </p>
                                )}
                              </>
                            );
                          } else if (zone1 > 0 && paidCases > 0) {
                            return (
                              <p className="text-xs text-purple-600 mt-0.5">
                                {paidCases} ücretli vaka × {zone1.toLocaleString('tr-TR')} ₺ = {(paidCases * zone1).toLocaleString('tr-TR')} ₺
                              </p>
                            );
                          }
                          return null;
                        })()}
                        {Number(monthlyStats[organization.id].total_revenue ?? 0) > 0 && (
                          <div className="mt-2 p-2 bg-purple-100 rounded-lg">
                            <p className="text-xs text-purple-800 font-medium">
                              Bu ay {Number(monthlyStats[organization.id].total_revenue).toLocaleString('tr-TR')} ₺ gelir elde edildi
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-xl">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <Target className="w-4 h-4 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <p className="text-sm font-medium text-orange-900">
                            Kalan Ücretsiz: {monthlyStats[organization.id].remaining_free} vaka
                          </p>
                          <span className="text-xs text-orange-600 font-medium">
                            {Math.round((monthlyStats[organization.id].total_cases / (monthlyStats[organization.id].monthly_limit || 50)) * 100)}%
                          </span>
                        </div>
                        <div className="w-full bg-orange-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              (monthlyStats[organization.id].total_cases / (monthlyStats[organization.id].monthly_limit || 50)) > 0.8 
                                ? 'bg-red-500' 
                                : (monthlyStats[organization.id].total_cases / (monthlyStats[organization.id].monthly_limit || 50)) > 0.6 
                                  ? 'bg-yellow-500' 
                                  : 'bg-green-500'
                            }`}
                            style={{
                              width: `${Math.min((monthlyStats[organization.id].total_cases / (monthlyStats[organization.id].monthly_limit || 50)) * 100, 100)}%`
                            }}
                          ></div>
                        </div>
                        <p className="text-xs text-orange-700 mt-1">
                          {monthlyStats[organization.id].monthly_limit || 50} vaka limiti
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Contact Info */}
                {organization.contact_person && (
                  <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-xl">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Users className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        {organization.contact_person}
                      </p>
                    </div>
                  </div>
                )}

                {organization.contact_phone && (
                  <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-xl">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Phone className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-purple-900">
                        {organization.contact_phone}
                      </p>
                    </div>
                  </div>
                )}

                {/* Address */}
                {organization.address && (
                  <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-xl">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <MapPin className="w-4 h-4 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-700 line-clamp-2">
                        {organization.address}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Terms */}
              {organization.payment_terms && (
                <div className="p-4 bg-yellow-50 rounded-xl mb-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <Star className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm font-medium text-yellow-900">
                      Ödeme Koşulları
                    </span>
                  </div>
                  <p className="text-sm text-yellow-800">
                    {organization.payment_terms}
                  </p>
                </div>
              )}

              {/* Action Button */}
              <Button
                onClick={() => openPricingModal(organization)}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl py-3"
                size="lg"
              >
                <DollarSign className="w-5 h-5 mr-2" />
                Fiyatlandırmayı Düzenle
              </Button>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredOrganizations.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-12 max-w-md mx-auto shadow-lg">
              <div className="p-6 bg-gray-100 rounded-3xl w-24 h-24 mx-auto mb-6">
                <Building className="w-12 h-12 text-gray-400 mx-auto mt-3" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Kurum bulunamadı
              </h3>
              <p className="text-gray-600 mb-6">
                Arama kriterlerinize uygun kurum bulunamadı.
              </p>
              {canManageInstitutions && (
                <Button 
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-2xl font-semibold px-6 py-3 transition-all duration-300"
                  onClick={openNewOrganizationModal}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  <span>İlk Kurumu Ekle</span>
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Pricing Modal */}
        {showPricingModal && selectedOrganization && (
          <OrganizationPricingModal
            organization={selectedOrganization}
            isOpen={showPricingModal}
            onClose={handlePricingModalClose}
            onSuccess={fetchOrganizations}
          />
        )}

        {/* New Organization Modal */}
        <NewOrganizationModal
          isOpen={showNewOrganizationModal}
          onClose={handleNewOrganizationModalClose}
          onSuccess={handleNewOrganizationSuccess}
        />
      </div>

      {/* Add CSS Animation */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default PricingOrganizationsPage; 